/**
 * <product-card-slider>
 * Loaded by sections/product-card-slider.liquid
 *
 * Swiper owns the slides. This component owns three things Swiper cannot do
 * well here:
 *
 *   1. It builds the Swiper from the section's settings, mobile-first, with one
 *      breakpoint at 768px — Symmetry's canonical one.
 *
 *   2. It builds the mobile bullet row by hand. Swiper's own `dynamicBullets`
 *      computes the bullet strip's translate from a *single fixed bullet width*,
 *      so a 52px active pill among 5px dots mis-centres the strip and the pill
 *      drifts as you page. Here each bullet's width is interpolated from how
 *      close the scroll position is to it, and the strip is translated so the
 *      active pill sits in the middle of a four-bullet window. The maths is the
 *      same as assets/collection-card-slider.js, driven off Swiper's `progress`
 *      instead of a raw scrollLeft.
 *
 *   3. It degrades. Swiper is loaded from assets/swiper.js in
 *      layout/theme.liquid, deferred, so it can simply not be there yet. In
 *      that case the component adds a class that turns the track into a plain
 *      horizontal scroller and stops — the paging is lost, the products are not.
 *
 * Bullets start out `hidden` in the markup, so a failure anywhere in here
 * leaves a scrolling row rather than a dead control.
 */
if (!customElements.get('product-card-slider')) {
  customElements.define(
    'product-card-slider',
    class ProductCardSlider extends HTMLElement {
      /**
       * The mobile edge inset, in px, passed to Swiper as slidesOffsetBefore /
       * slidesOffsetAfter so the first card lines up with the container's text
       * and the last scrolls out under the viewport edge.
       *
       * This is the value of `--container-pad-x`, which is 16px at every width
       * below 768px (main.css:410-418) — and 768px is exactly where this
       * component stops using the offsets. It is a literal rather than a read
       * of the custom property because the property has to be read at the
       * breakpoint it applies to, and the component may well initialise at
       * desktop width, where it reports 50-60px.
       *
       * Padding on the track cannot be used instead: Swiper measures its
       * container with `clientWidth`, which includes padding, so padding
       * corrupts the slide-width maths and lets slides sit under the inset.
       */
      static MOBILE_EDGE = 16;

      /** Bullets visible in the window at once. */
      static DOT_WINDOW = 4;

      connectedCallback() {
        this.swiperEl = this.querySelector('[data-pcs-swiper]');
        if (!this.swiperEl) return;

        if (typeof Swiper === 'undefined') {
          this.classList.add('product-card-slider--no-swiper');
          return;
        }

        this.dotsEl = this.querySelector('[data-pcs-dots]');
        this.stripEl = this.querySelector('[data-pcs-strip]');
        this.dots = [];
        this.frame = null;
        this.windowWidth = 0;

        this.handleProgress = this.handleProgress.bind(this);
        this.handleRefresh = this.refresh.bind(this);
        this.handleDotClick = this.handleDotClick.bind(this);

        if (this.stripEl) this.stripEl.addEventListener('click', this.handleDotClick);

        this.swiper = new Swiper(this.swiperEl, {
          slidesPerView: this.number('slidesMobile', 1.5),
          // Read from the stylesheet, not from a data attribute: the gap is a
          // clamp() above 768px, so its value changes continuously with the
          // viewport and only the computed style knows it. syncGap() keeps this
          // in step afterwards.
          spaceBetween: this.gapPx,
          slidesOffsetBefore: ProductCardSlider.MOBILE_EDGE,
          slidesOffsetAfter: ProductCardSlider.MOBILE_EDGE,

          // No loop and no autoplay: neither is in the brief or the design, and
          // a loop would make the bullet row's page count a moving target.
          loop: false,

          // Locks the slider and hides the arrows when every slide already
          // fits. `.swiper-button-lock { display: none }` is unscoped in
          // Swiper's stylesheet, so the custom arrows inherit that for free.
          watchOverflow: true,

          a11y: {
            // Swiper's a11y module writes aria-label onto the navigation
            // buttons, which would replace the theme's translated strings with
            // its own English defaults. Handing it the translations keeps the
            // section localised.
            prevSlideMessage: this.dataset.prevLabel || 'Previous',
            nextSlideMessage: this.dataset.nextLabel || 'Next',
          },

          navigation: {
            prevEl: this.querySelector('[data-pcs-prev]'),
            nextEl: this.querySelector('[data-pcs-next]'),
          },

          breakpoints: {
            768: {
              slidesPerView: this.number('slidesDesktop', 5),
              // No spaceBetween here on purpose. A value in a breakpoint is
              // reapplied every time that breakpoint is entered, which would
              // stamp a fixed number back over the fluid one on the first
              // resize past 768px. syncGap() owns this property outright.
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            },
          },

          on: {
            progress: this.handleProgress,

            // `snapGridLengthChange` is the authoritative one: Swiper emits it
            // from inside updateSlides(), *after* snapGrid has been rebuilt and
            // (with watchOverflow on) after checkOverflow has set isLocked. The
            // other two fire before that work — Swiper's resize module emits
            // `resize`, and the core then reacts to it by recomputing — so on
            // their own they would read a stale page count. They are kept
            // because they also cover a width change that leaves the page count
            // untouched, where the authoritative event never fires.
            snapGridLengthChange: this.handleRefresh,
            breakpoint: this.handleRefresh,
            resize: this.handleRefresh,
          },
        });

        this.refresh();
      }

      disconnectedCallback() {
        if (this.stripEl) this.stripEl.removeEventListener('click', this.handleDotClick);
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
      }

      /**
       * @param {string} key - data attribute name, camelCased.
       * @param {number} fallback - value to use when the attribute is absent or unparseable.
       * @returns {number}
       */
      number(key, fallback) {
        const value = parseFloat(this.dataset[key]);
        return Number.isFinite(value) && value > 0 ? value : fallback;
      }

      /**
       * Bullet geometry, read from the stylesheet so the design constants stay
       * in one place. Read on refresh rather than per frame.
       * @returns {{size: number, active: number, gap: number}}
       */
      get metrics() {
        const styles = getComputedStyle(this);
        const read = (name, fallback) => {
          const value = parseFloat(styles.getPropertyValue(name));
          return Number.isFinite(value) ? value : fallback;
        };

        return {
          size: read('--pcs-dot-size', 5),
          active: read('--pcs-dot-active', 52),
          gap: read('--pcs-dot-gap', 6),
        };
      }

      /**
       * Rebuild the bullet row for the current breakpoint. Safe to call
       * repeatedly — Swiper fires `resize` and `breakpoint` into it.
       */
      /**
       * The gap between slides, in px, as the stylesheet currently computes it.
       * Above 768px that is a clamp() against the viewport, so it is a moving
       * number rather than a setting. Custom properties inherit, so reading it
       * from this element picks up the value declared on the section wrapper.
       * @returns {number}
       */
      get gapPx() {
        const declared = parseFloat(getComputedStyle(this).getPropertyValue('--pcs-gap'));
        // The stylesheet can still be media="print" when this first runs
        // (lazy-stylesheet-attrs defers it below position 2), so fall back to
        // the section's own setting rather than to nothing.
        if (Number.isFinite(declared)) return declared;
        return this.number('gapMobile', 16);
      }

      /**
       * Push the stylesheet's current gap into Swiper, which needs it as a
       * number to compute slide widths. Compared against Swiper's own live
       * parameter rather than a cached copy, so a breakpoint change that
       * reapplies its own params cannot leave the two out of step.
       */
      syncGap() {
        const gap = this.gapPx;
        if (!Number.isFinite(gap) || gap === this.swiper.params.spaceBetween) return;

        this.swiper.params.spaceBetween = gap;
        // update() does not emit `resize`, so this cannot re-enter refresh().
        this.swiper.update();
      }

      refresh() {
        // Swiper's own init() runs inside the constructor and emits into these
        // handlers before the assignment to this.swiper has completed, so this
        // guard is load-bearing, not defensive. The explicit refresh() after
        // construction is what does the first build.
        if (!this.swiper) return;

        // Before the early returns below: the gap has to track the viewport
        // whether or not this slider has a bullet row.
        this.syncGap();

        if (!this.dotsEl || !this.stripEl) return;

        // The bullets are a mobile control; the stylesheet hides them at 768px.
        // Asking the stylesheet rather than re-testing the breakpoint here keeps
        // the breakpoint defined in one place.
        if (getComputedStyle(this.dotsEl).display === 'none') {
          this.reset();
          return;
        }

        const pages = this.swiper.snapGrid ? this.swiper.snapGrid.length : 0;

        if (this.swiper.isLocked || pages < 2) {
          this.reset();
          return;
        }

        if (pages !== this.dots.length) this.buildDots(pages);

        // The two bullets either side of the scroll position always carry
        // weights summing to exactly 1, so the strip's total width — and any
        // window within it — is constant during a swipe. That makes both of
        // these fixed numbers rather than per-frame measurements.
        const { size, active, gap } = this.metrics;
        const expansion = active - size;
        const strip = pages * size + expansion + (pages - 1) * gap;
        const full = ProductCardSlider.DOT_WINDOW * size + expansion
          + (ProductCardSlider.DOT_WINDOW - 1) * gap;

        this.stripWidth = strip;
        this.windowWidth = Math.min(strip, full);
        this.dotsEl.style.width = `${this.windowWidth}px`;
        this.dotsEl.hidden = false;

        this.update();
      }

      /** Hide and empty the bullet row. */
      reset() {
        this.dotsEl.hidden = true;
        this.dotsEl.style.removeProperty('width');
        this.stripEl.style.removeProperty('transform');
        this.stripEl.replaceChildren();
        this.dots = [];
      }

      /**
       * @param {number} pages - Number of bullets to render.
       */
      buildDots(pages) {
        const label = this.dataset.pageLabel || 'Page [page]';

        this.dots = Array.from({ length: pages }, (item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-card-slider__dot';
          dot.dataset.index = index;
          dot.setAttribute('aria-label', label.replace('[page]', index + 1));
          return dot;
        });

        this.stripEl.replaceChildren(...this.dots);
      }

      handleProgress() {
        if (this.frame) return;

        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      /**
       * Weight every bullet by how close the scroll position is to it — 1 when
       * it is the resting page, 0 once the scroll is a whole page away, and
       * everything in between mid-swipe — then translate the strip so the point
       * between the two straddled bullets sits in the middle of the window.
       *
       * The translate is clamped to the strip's own ends, which is what Swiper's
       * dynamic bullets do too: without it the row would open empty space
       * before the first bullet and after the last. The visible consequence is
       * deliberate — the active pill rests left of centre on the first page and
       * right of centre on the last.
       */
      update() {
        if (!this.dots.length || !this.swiper) return;

        const { size, active, gap } = this.metrics;
        const position = this.swiper.progress * (this.dots.length - 1);
        const resting = Math.round(position);
        const centres = [];
        let offset = 0;

        this.dots.forEach((dot, index) => {
          const weight = Math.max(0, 1 - Math.abs(position - index));
          const width = size + weight * (active - size);

          centres.push(offset + width / 2);
          offset += width + gap;

          dot.style.setProperty('--pcs-dot-weight', weight.toFixed(3));
          if (index === resting) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        // Interpolate the centre between the two straddled bullets, so the
        // strip glides with the finger instead of stepping bullet to bullet.
        const lower = Math.max(0, Math.min(this.dots.length - 1, Math.floor(position)));
        const upper = Math.min(this.dots.length - 1, lower + 1);
        const target = centres[lower] + (centres[upper] - centres[lower]) * (position - lower);

        const translate = Math.min(
          0,
          Math.max(this.windowWidth - this.stripWidth, this.windowWidth / 2 - target)
        );

        this.stripEl.style.transform = `translate3d(${translate.toFixed(2)}px, 0, 0)`;
      }

      /**
       * @param {object} evt - Click event.
       */
      handleDotClick(evt) {
        const dot = evt.target.closest('.product-card-slider__dot');
        if (!dot || !this.swiper) return;

        // One bullet per snap position, and with slidesPerGroup at its default
        // of 1 each snap position is a slide's own start — so the bullet index
        // is the slide index.
        this.swiper.slideTo(Number(dot.dataset.index));
      }
    }
  );
}
